import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { Spinner, Container } from 'react-bootstrap';
import DashboardLayout from '../dashboard/DashboardLayout';

const SmartRedirect: React.FC = () => {
  const { user, loading } = useSelector((state: RootState) => state.auth);
  const location = useLocation();

  if (loading || !user) {
    return (
      <Container fluid className="vh-100 d-flex align-items-center justify-content-center">
        <div className="text-center">
          <Spinner animation="border" variant="primary" />
          <div className="mt-2 text-muted">Loading...</div>
        </div>
      </Container>
    );
  }

  // Get the intended destination from location state (from login redirect)
  const from = (location.state as any)?.from?.pathname;
  
  // Check if user has access to the intended destination
  const hasAccessToPage = (path: string, userRole: string): boolean => {
    // Define role-based page access (must match App.tsx route protections)
    const roleRequirements: { [key: string]: string[] } = {
      '/dashboard/team': ['admin'],
      '/dashboard/individual': ['admin'],
      '/commissions': ['admin'],
      '/pipeline': ['admin'],
      '/deals': ['admin'],
      '/schedule': ['salesperson', 'sales_manager', 'vp_sales', 'admin', 'events_manager', 'marketing'],
      '/customers': ['salesperson', 'sales_manager', 'vp_sales', 'admin', 'events_manager', 'finance', 'marketing'],
      '/sling-locations': ['sales_manager', 'vp_sales', 'admin', 'events_manager', 'marketing'],
      '/analytics': ['admin'],
      '/invoices': ['admin'],
      '/payments': ['admin'],
      '/reports': ['admin'],
      '/email-marketing': ['salesperson', 'sales_manager', 'vp_sales', 'admin', 'events_manager', 'marketing'],
      '/admin/users': ['admin'],
      '/admin/settings': ['admin'],
    };

    // Check if the path has role requirements (exact match or prefix match)
    const requiredRoles = roleRequirements[path]
      || Object.entries(roleRequirements).find(([prefix]) => path.startsWith(prefix + '/'))?.[1];
    if (requiredRoles) {
      return requiredRoles.includes(userRole);
    }

    // If no specific requirements, allow access
    return true;
  };
  
  // If there's a specific page they were trying to access and they have access, go there
  if (from && from !== '/login' && from !== '/' && from !== '/dashboard' && user.role && hasAccessToPage(from, user.role)) {
    return <Navigate to={from} replace />;
  }

  // Handle users with "none" role - they have no access
  if (user.role === 'none') {
    return (
      <DashboardLayout
        title="Pending Approval"
        subtitle="Your account is awaiting administrator approval"
      >
        <div className="text-center py-5">
          <i className="bi bi-hourglass-split fs-1 text-warning mb-3 d-block"></i>
          <h3 className="text-warning mb-3">Account Pending Approval</h3>
          <p className="text-muted mb-4">
            Your account has been created but does not yet have access to any features.
            <br />
            An administrator has been notified and will assign you appropriate permissions soon.
            <br />
            <br />
            Please check back later or contact your administrator if you have any questions.
          </p>
          <div className="d-flex gap-2 justify-content-center">
            <a href="/profile" className="btn btn-outline-primary">
              <i className="bi bi-person me-2"></i>
              View Profile
            </a>
          </div>
        </div>
      </DashboardLayout>
    );
  }

  // Only redirect based on user role for root paths
  switch (user.role) {
    case 'admin':
      return <Navigate to="/schedule" replace />;
    case 'vp_sales':
    case 'sales_manager':
    case 'finance':
      return <Navigate to="/customers" replace />;
    case 'salesperson':
    case 'events_manager':
    case 'marketing':
    default:
      return <Navigate to="/schedule" replace />;
  }
};

export default SmartRedirect;